import type { IComponentHandler, IComponentHandlerCallbacks, IInsertData } from "../../Header.ts";
import { AbstractComponentHandler } from "../AbstractComponentHandler.ts";

/**
 * Handler for simple input texts components, such as `<p:inputText/>` or `<p:inputTextArea/>`.
 * @member {string} selector CSS selector for the input or textarea element.
 * Should be either `input` or `textarea`.
 */
export class SimpleInputHandler extends AbstractComponentHandler implements IComponentHandler {
  private _selector: string;

  /**
   * @param selector CSS selector for selecting the input
   * or textarea element.
   */
  constructor(selector: string) {
    super();
    this._selector = selector;
  }

  override accepts($element: JQuery): boolean {
    return this._getInput($element).length === 1;
  }

  override focus($element: JQuery): void {
    this._getInput($element).trigger("focus");
  }

  override getInputElement($element: JQuery): JQuery {
    return this._getInput($element);
  }

  override initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): boolean {
    const $input = this._getInput($element);
    $input.on("keyup", (event) => {
      if (event.key === " " && (event.ctrlKey || event.metaKey)) {
        callbacks.onPlaceholderRequested();
      }
    });
    $input.on("focus", () => callbacks.onFocus());
    return true;
  }

  override insertText($element: JQuery, data: IInsertData): void {
    const $input = this._getInput($element);
    const pos = $input.textrange("get");
    const adjustedText = pos.length === 0 ? `${data.content} ` : data.content;
    $input.textrange("replace", adjustedText);
    if (pos.length === 0) {
      $input.textrange("set", pos.end + adjustedText.length, 0);
    }
  }

  /**
   * @param $element Input component.
   * @returns The DOM input element (input or textarea).
   */
  private _getInput($element: JQuery): JQuery {
    return $element
      .find(this._selector)
      .addBack(this._selector)
      .filter((_, el) => el.closest(".ui-helper-hidden") === null);
  }
}
